import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_STRING as api } from '@/App';
import { toast } from 'sonner';
import { Sun, Moon } from 'lucide-react';
import { useDarkMode } from '@/hooks/useDarkMode';
import { useTheme } from '@/contexts/ThemeContext';

import { logger } from '@/utils/logger';
import { BadgeCheck, Bell, Calendar, Edit, Lock, LogOut, Mail, MailWarning, MessageSquare, Monitor, Settings, Sparkles, User } from 'lucide-react';
import { PasswordInput } from '@/components/auth/PasswordInput';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
export default function Profile({ user, setUser }) {
  const navigate = useNavigate();
  const darkMode = useDarkMode();
  const { theme, setTheme } = useTheme();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  
  const [editForm, setEditForm] = useState({
    name: user?.name || '',
    email: user?.email || ''
  });
  
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Email preferences state
  const [emailPreferences, setEmailPreferences] = useState({
    marketing_emails: true,
    occasion_reminders: true,
    reminder_timing: [60, 30, 14, 7],
    in_app_notifications_enabled: true
  });
  const [savingPreferences, setSavingPreferences] = useState(false);
  
  // Delete account state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchStats();
    fetchPreferences();
  }, [user, navigate]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${api}/profile/stats`, {
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      logger.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPreferences = async () => {
    try {
      const response = await fetch(`${api}/profile/preferences`, {
      });

      if (response.ok) {
        const data = await response.json();
        setEmailPreferences(data);
      }
    } catch (error) {
      logger.error('Failed to fetch preferences:', error);
    }
  };

  const handleEditProfile = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${api}/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        const updatedUser = await response.json();
        setUser(updatedUser);
        setShowEditDialog(false);
        toast.success('Profile updated successfully!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update profile');
      }
    } catch (error) {
      logger.error('Update error:', error);
      toast.error('Failed to update profile');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    try {
      const response = await fetch(`${api}/profile/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        })
      });

      if (response.ok) {
        setShowPasswordDialog(false);
        setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
        toast.success('Password changed successfully!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to change password');
      }
    } catch (error) {
      logger.error('Password change error:', error);
      toast.error('Failed to change password');
    }
  };

  const handleSavePreferences = async () => {
    setSavingPreferences(true);
    try {
      const response = await fetch(`${api}/profile/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailPreferences)
      });

      if (response.ok) {
        toast.success('Preferences saved successfully!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save preferences');
      }
    } catch (error) {
      logger.error('Save preferences error:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSavingPreferences(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
    toast.success('Logged out successfully');
  };

  const [resendingVerification, setResendingVerification] = useState(false);

  const handleResendVerification = async () => {
    setResendingVerification(true);
    try {
      const response = await fetch(`${api}/auth/resend-verification`, { method: 'POST' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to send');
      toast.success('Verification email sent! Check your inbox (and spam folder).');
    } catch (error) {
      logger.error('Error resending verification:', error);
      toast.error(error.message || 'Could not send the verification email');
    } finally {
      setResendingVerification(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    
    if (!deletePassword) {
      toast.error('Please enter your password');
      return;
    }

    setIsDeleting(true);
    try {
      const response = await fetch(`${api}/profile/delete-account?password=${encodeURIComponent(deletePassword)}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        toast.success('Account deleted successfully');
        localStorage.removeItem('token');
        setUser(null);
        navigate('/');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete account');
      }
    } catch (error) {
      logger.error('Delete account error:', error);
      toast.error('Failed to delete account');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center transition-colors" style={{ background: darkMode.bgPrimary }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9E6752] mx-auto"></div>
          <p className="mt-4 transition-colors" style={{ color: darkMode.textSecondary }}>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen transition-colors page-enter" style={{ background: darkMode.bgPrimary }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Header */}
        <div className="mb-6 sm:mb-8 page-rise-1">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2 flex items-center gap-3 transition-colors" style={{ color: darkMode.textPrimary }}>
            <User className="w-8 h-8" style={{ color: '#9E6752' }} />
            My Profile
          </h1>
          <p className="text-base sm:text-lg transition-colors" style={{ color: darkMode.textSecondary }}>
            Manage your account settings and preferences
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-6">
          {/* Card 1, Account Information */}
          <div className="rounded-xl p-4 sm:p-6 transition-colors page-rise-3" style={{
            background: darkMode.isDark ? '#2D4354' : 'white',
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
          }}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-5">
              <div className="flex items-center gap-2 mb-3 sm:mb-0">
                <div className="p-1.5 rounded-lg" style={{ background: '#FED7A5' }}>
                  <User className="w-4 h-4" style={{ color: '#9E6752' }} />
                </div>
                <h2 className="text-lg sm:text-xl font-bold transition-colors" style={{ color: darkMode.textPrimary }}>
                  Account
                </h2>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    setEditForm({ name: user.name, email: user.email });
                    setShowEditDialog(true);
                  }}
                  size="sm"
                  className="flex items-center gap-2 transition-all hover:opacity-90 font-semibold hover:transform-none"
                  style={{ background: '#9E6752', color: 'white' }}
                  data-testid="profile-edit-btn"
                >
                  <Edit className="w-4 h-4" />
                  <span className="hidden sm:inline">Edit Profile</span>
                  <span className="sm:hidden">Edit</span>
                </Button>
                <Button
                  onClick={() => setShowPasswordDialog(true)}
                  size="sm"
                  className="flex items-center gap-2 transition-all hover:opacity-90 font-semibold hover:transform-none"
                  style={{
                    background: darkMode.isDark ? '#FED7A5' : '#2D4354',
                    color: darkMode.isDark ? '#20212B' : 'white'
                  }}
                  data-testid="profile-password-btn"
                >
                  <Lock className="w-4 h-4" />
                  <span className="hidden sm:inline">Change Password</span>
                  <span className="sm:hidden">Password</span>
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="flex items-start gap-3 p-3 rounded-lg transition-colors" style={{
                background: darkMode.isDark ? 'rgba(254, 215, 165, 0.1)' : '#F9FAFB'
              }}>
                <User className="w-5 h-5 mt-0.5 transition-colors" style={{ color: darkMode.textSecondary }} />
                <div className="min-w-0">
                  <p className="text-xs font-medium uppercase tracking-wide transition-colors" style={{ color: darkMode.textSecondary }}>Name</p>
                  <p className="text-sm font-semibold mt-0.5 truncate transition-colors" style={{ color: darkMode.textPrimary }}>{user?.name}</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-lg transition-colors" style={{
                background: darkMode.isDark ? 'rgba(254, 215, 165, 0.1)' : '#F9FAFB'
              }}>
                <Mail className="w-5 h-5 mt-0.5 transition-colors" style={{ color: darkMode.textSecondary }} />
                <div className="min-w-0">
                  <p className="text-xs font-medium uppercase tracking-wide transition-colors" style={{ color: darkMode.textSecondary }}>Email</p>
                  <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
                    <p className="text-sm font-semibold truncate transition-colors" style={{ color: darkMode.textPrimary }}>{user?.email}</p>
                    {user?.email_verified ? (
                      <span
                        data-testid="email-verified-badge"
                        title="Email verified"
                        className="flex items-center gap-1 shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold"
                        style={{ background: 'rgba(34,197,94,0.15)', color: '#16A34A' }}
                      >
                        <BadgeCheck className="w-3.5 h-3.5" />
                        Verified
                      </span>
                    ) : (
                      <span
                        data-testid="email-unverified-badge"
                        title="Email not verified"
                        className="flex items-center gap-1 shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold"
                        style={{ background: '#FFF4E5', color: '#9E6752' }}
                      >
                        <MailWarning className="w-3.5 h-3.5" />
                        Not verified
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-lg transition-colors" style={{
                background: darkMode.isDark ? 'rgba(254, 215, 165, 0.1)' : '#F9FAFB'
              }}>
                <Calendar className="w-5 h-5 mt-0.5 transition-colors" style={{ color: darkMode.textSecondary }} />
                <div className="min-w-0">
                  <p className="text-xs font-medium uppercase tracking-wide transition-colors" style={{ color: darkMode.textSecondary }}>Member Since</p>
                  <p className="text-sm font-semibold mt-0.5 transition-colors" style={{ color: darkMode.textPrimary }}>
                    {formatDate(stats?.member_since)}
                  </p>
                </div>
              </div>
            </div>

            {/* Email verification call-to-action */}
            {user && !user.email_verified && (
              <div
                data-testid="verify-email-panel"
                className="mt-4 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                style={{ background: '#FFF4E5', border: '1px solid #FED7A5' }}
              >
                <div className="flex items-start gap-3 flex-1">
                  <MailWarning className="w-5 h-5 mt-0.5 shrink-0" style={{ color: '#9E6752' }} />
                  <div>
                    <p className="text-sm font-semibold" style={{ color: '#9E6752' }}>
                      Verify your email address
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: '#73766A' }}>
                      We sent a verification link to {user.email}. Click the link in that email to verify.
                      Can't find it? Check spam, or resend it below.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleResendVerification}
                  disabled={resendingVerification}
                  size="sm"
                  data-testid="profile-resend-verification-btn"
                  className="shrink-0 font-semibold hover:opacity-90 hover:transform-none"
                  style={{ background: '#9E6752', color: 'white' }}
                >
                  {resendingVerification ? 'Sending...' : 'Resend verification email'}
                </Button>
              </div>
            )}
          </div>

          {/* Card 3, Preferences (Appearance + Emails + Notifications) */}
          <div className="rounded-xl p-4 sm:p-6 transition-colors page-rise-5" style={{
            background: darkMode.isDark ? '#2D4354' : 'white',
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
          }}>
            <div className="flex items-center gap-2 mb-5">
              <div className="p-1.5 rounded-lg" style={{ background: '#FED7A5' }}>
                <Settings className="w-4 h-4" style={{ color: '#9E6752' }} />
              </div>
              <h2 className="text-lg sm:text-xl font-bold transition-colors" style={{ color: darkMode.textPrimary }}>
                Preferences
              </h2>
            </div>

            <div className="space-y-4">
              {/* Appearance */}
              <div className="flex items-start gap-3 p-3 rounded-lg transition-colors" style={{
                background: darkMode.isDark ? 'rgba(254, 215, 165, 0.1)' : '#F9FAFB'
              }}>
                <div className="p-2 rounded-lg" style={{ background: '#FED7A5' }}>
                  <Monitor className="w-4 h-4" style={{ color: '#9E6752' }} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-sm mb-2 transition-colors" style={{ color: darkMode.textPrimary }}>
                    Appearance
                  </h3>
                  <div className="flex gap-2">
                    {[
                      { value: 'light', label: 'Light', icon: Sun },
                      { value: 'dark', label: 'Dark', icon: Moon }
                    ].map((option) => {
                      const Icon = option.icon;
                      const isActive = theme === option.value;
                      return (
                        <button
                          key={option.value}
                          onClick={() => setTheme(option.value)}
                          className="flex-1 px-3 py-2 rounded-lg font-medium transition-all border text-xs hover:transform-none"
                          style={{
                            background: isActive ? '#9E6752' : 'transparent',
                            color: isActive ? 'white' : darkMode.textPrimary,
                            borderColor: isActive ? '#9E6752' : darkMode.borderPrimary
                          }}
                          data-testid={`theme-toggle-${option.value}`}
                        >
                          <Icon className="w-4 h-4 mx-auto mb-0.5" />
                          <span>{option.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Marketing Emails */}
              <div className="flex items-start gap-3 p-3 rounded-lg transition-colors" style={{
                background: darkMode.isDark ? 'rgba(254, 215, 165, 0.1)' : '#F9FAFB'
              }}>
                <div className="p-2 rounded-lg" style={{ background: '#FED7A5' }}>
                  <Mail className="w-4 h-4" style={{ color: '#9E6752' }} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-base transition-colors" style={{ color: darkMode.textPrimary }}>
                      Marketing Emails
                    </h3>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={emailPreferences.marketing_emails}
                        onChange={(e) => setEmailPreferences({ ...emailPreferences, marketing_emails: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#9E6752]"></div>
                    </label>
                  </div>
                  <p className="text-sm transition-colors" style={{ color: darkMode.textSecondary }}>
                    Receive promotional emails and special offers
                  </p>
                </div>
              </div>

              {/* Occasion Reminders */}
              <div className="flex items-start gap-3 p-3 rounded-lg transition-colors" style={{
                background: darkMode.isDark ? 'rgba(254, 215, 165, 0.1)' : '#F9FAFB'
              }}>
                <div className="p-2 rounded-lg" style={{ background: '#FED7A5' }}>
                  <Bell className="w-4 h-4" style={{ color: '#9E6752' }} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-base transition-colors" style={{ color: darkMode.textPrimary }}>
                      Occasion Reminders
                    </h3>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={emailPreferences.occasion_reminders}
                        onChange={(e) => setEmailPreferences({ ...emailPreferences, occasion_reminders: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#9E6752]"></div>
                    </label>
                  </div>
                  <p className="text-sm mb-2 transition-colors" style={{ color: darkMode.textSecondary }}>
                    Get automatic reminders for special occasions
                  </p>

                  {emailPreferences.occasion_reminders && (
                    <div className="mt-2 p-2 rounded-lg transition-colors" style={{
                      background: darkMode.isDark ? 'rgba(0,0,0,0.2)' : 'rgba(254, 215, 165, 0.2)'
                    }}>
                      <p className="text-sm font-medium mb-2 transition-colors" style={{ color: darkMode.textPrimary }}>
                        When should we remind you?
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {[
                          { value: 60, label: '2 months' },
                          { value: 30, label: '1 month' },
                          { value: 21, label: '3 weeks' },
                          { value: 14, label: '2 weeks' },
                          { value: 7, label: '1 week' },
                          { value: 3, label: '3 days' },
                          { value: 1, label: '1 day' }
                        ].map(option => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => {
                              const timing = emailPreferences.reminder_timing || [];
                              if (timing.includes(option.value)) {
                                setEmailPreferences({
                                  ...emailPreferences,
                                  reminder_timing: timing.filter(d => d !== option.value)
                                });
                              } else {
                                setEmailPreferences({
                                  ...emailPreferences,
                                  reminder_timing: [...timing, option.value].sort((a, b) => b - a)
                                });
                              }
                            }}
                            className="px-3 py-1.5 rounded-full text-sm font-medium transition-all hover:transform-none"
                            style={{
                              background: (emailPreferences.reminder_timing || []).includes(option.value) ? '#9E6752' : darkMode.isDark ? 'rgba(255,255,255,0.1)' : 'white',
                              color: (emailPreferences.reminder_timing || []).includes(option.value) ? 'white' : darkMode.textPrimary,
                              border: `1px solid ${(emailPreferences.reminder_timing || []).includes(option.value) ? '#9E6752' : darkMode.borderPrimary}`
                            }}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* In-App Notifications */}
              <div className="flex items-start gap-3 p-3 rounded-lg transition-colors" style={{
                background: darkMode.isDark ? 'rgba(254, 215, 165, 0.1)' : '#F9FAFB'
              }}>
                <div className="p-2 rounded-lg" style={{ background: '#FED7A5' }}>
                  <MessageSquare className="w-4 h-4" style={{ color: '#9E6752' }} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-base transition-colors" style={{ color: darkMode.textPrimary }}>
                      In-App Notifications
                    </h3>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={emailPreferences.in_app_notifications_enabled}
                        onChange={(e) => setEmailPreferences({ ...emailPreferences, in_app_notifications_enabled: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#9E6752]"></div>
                    </label>
                  </div>
                  <p className="text-sm transition-colors" style={{ color: darkMode.textSecondary }}>
                    Receive in-app notifications for all system events
                  </p>
                </div>
              </div>

              {/* Save Preferences Button */}
              <Button
                onClick={handleSavePreferences}
                disabled={savingPreferences}
                className="w-full font-semibold hover:transform-none"
                style={{ background: '#9E6752', color: 'white' }}
                data-testid="profile-save-preferences-btn"
              >
                {savingPreferences ? 'Saving...' : 'Save Preferences'}
              </Button>
            </div>
          </div>

          {/* Card 4, Subscription + Account actions */}
          <div className="rounded-xl p-4 sm:p-6 transition-colors" style={{
            background: darkMode.isDark ? '#2D4354' : 'white',
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
          }}>
            <div className="flex items-center gap-2 mb-5">
              <div className="p-1.5 rounded-lg" style={{ background: '#FED7A5' }}>
                <Sparkles className="w-4 h-4" style={{ color: '#9E6752' }} />
              </div>
              <h2 className="text-lg sm:text-xl font-bold transition-colors" style={{ color: darkMode.textPrimary }}>
                Subscription &amp; Account
              </h2>
            </div>

            <Button
              data-testid="profile-pricing-btn"
              onClick={() => navigate('/pricing')}
              className="w-full flex items-center justify-center gap-2 font-semibold transition-all hover:opacity-90 mb-3 hover:transform-none"
              style={{
                background: 'linear-gradient(135deg, #FED7A5 0%, #E8C795 100%)',
                color: '#20212B',
                border: 'none'
              }}
            >
              <Sparkles className="w-4 h-4" />
              View Plans &amp; Pricing
            </Button>
            <Button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 font-semibold transition-all hover:opacity-90 mb-3 hover:transform-none"
              style={{ background: '#DC2626', color: 'white' }}
              data-testid="profile-logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Log Out
            </Button>

            {/* Danger zone, visually subordinated to the safer actions
                above with a thin top divider so the destructive action
                doesn't sit at the same level as logout. */}
            <div className="mt-4 pt-4 transition-colors" style={{ borderTop: `1px dashed ${darkMode.borderPrimary}` }}>
              <Button
                onClick={() => setShowDeleteDialog(true)}
                className="w-full flex items-center justify-center gap-2 font-semibold transition-all hover:opacity-90 hover:transform-none"
                style={{
                  background: darkMode.isDark ? 'rgba(239, 68, 68, 0.2)' : '#FEE2E2',
                  color: '#DC2626',
                  border: '1px solid #DC2626'
                }}
                data-testid="profile-delete-btn"
              >
                <User className="w-4 h-4" />
                Delete Account
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Profile Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Profile</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditProfile} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Name</label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                required
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                required
                placeholder="your@email.com"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" style={{ background: '#9E6752', color: 'white' }}>
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Current Password</label>
              <PasswordInput
                testId="profile-current-password-input"
                toggleTestId="profile-current-password-toggle"
                autoComplete="current-password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                placeholder="Enter current password"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">New Password</label>
              <PasswordInput
                testId="profile-new-password-input"
                toggleTestId="profile-new-password-toggle"
                autoComplete="new-password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                placeholder="Enter new password"
              />
              <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Confirm New Password</label>
              <PasswordInput
                testId="profile-confirm-password-input"
                toggleTestId="profile-confirm-password-toggle"
                autoComplete="new-password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                placeholder="Confirm new password"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button 
                type="button" 
                onClick={() => setShowPasswordDialog(false)}
                style={{ 
                  background: '#F3F4F6', 
                  color: '#534145',
                  border: '1px solid #D1D5DB'
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                style={{ 
                  background: '#9E6752', 
                  color: 'white',
                  fontWeight: '600'
                }}
                className="hover:opacity-90 transition-opacity"
              >
                Change Password
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-md" style={{ background: darkMode.isDark ? '#2D4354' : 'white' }}>
          <DialogHeader>
            <DialogTitle style={{ color: darkMode.textPrimary }}>Delete Account</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleDeleteAccount} className="space-y-4">
            <div className="p-4 rounded-lg" style={{ background: '#FEE2E2', border: '1px solid #DC2626' }}>
              <p className="text-sm font-semibold text-red-800 mb-2">⚠️ Warning: This action cannot be undone!</p>
              <p className="text-sm text-red-700">
                Deleting your account will permanently remove:
              </p>
              <ul className="text-sm text-red-700 list-disc list-inside mt-2 space-y-1">
                <li>All your keepsakes and saved gifts</li>
                <li>All wishlists and preferences</li>
                <li>All questionnaires and responses</li>
                <li>Your entire account data</li>
              </ul>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: darkMode.textPrimary }}>
                Enter your password to confirm
              </label>
              <PasswordInput
                testId="profile-delete-password-input"
                toggleTestId="profile-delete-password-toggle"
                autoComplete="current-password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Enter your password"
                style={{
                  background: darkMode.isDark ? 'rgba(255,255,255,0.1)' : 'white',
                  color: darkMode.textPrimary,
                  borderColor: '#DC2626'
                }}
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button 
                type="button" 
                onClick={() => {
                  setShowDeleteDialog(false);
                  setDeletePassword('');
                }}
                style={{ 
                  background: '#F3F4F6', 
                  color: '#534145',
                  border: '1px solid #D1D5DB'
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                disabled={isDeleting}
                style={{ 
                  background: '#DC2626', 
                  color: 'white',
                  fontWeight: '600'
                }}
                className="hover:opacity-90 transition-opacity"
              >
                {isDeleting ? 'Deleting...' : 'Delete Account'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
